import { Component, inject, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '@services/auth.service';
import { UserModel } from '@models/user/user.model';

@Component({
  selector: 'app-auth-credentials-form',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule],
  host: { class: 'block w-full' },
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './auth-credentials-form.component.html',
})
export class AuthCredentialsFormComponent {
  #fb = inject(FormBuilder);
  #auth = inject(AuthService);
  #toast = inject(ToastrService);
  #translate = inject(TranslateService);

  success = output<UserModel>();

  form = this.#fb.nonNullable.group({
    identifier: ['', Validators.required],
    password: ['', Validators.required],
  });

  working = signal(false);
  credentialsRejected = signal(false);
  statusIcon = signal('check');
  statusLabel = signal('login.connection_title');
  rotating = signal(false);

  submit(): void {
    if (this.working()) return;

    if (this.form.controls.identifier.invalid) {
      this.form.controls.identifier.markAsTouched();
      this.#flash('login.errors.invalid_identifier', false);
      return;
    }

    if (this.form.controls.password.invalid) {
      this.form.controls.password.markAsTouched();
      this.#flash('login.errors.invalid_password', false);
      return;
    }

    this.working.set(true);
    this.credentialsRejected.set(false);
    this.statusIcon.set('sync');
    this.statusLabel.set('login.connection_message');
    this.rotating.set(true);

    const { identifier, password } = this.form.getRawValue();

    this.#auth.login(identifier, password).subscribe({
      next: (user) => {
        this.#toast.success(
          this.#translate.instant('login.toast.success_title'),
          this.#translate.instant('login.toast.success_message'),
        );
        this.working.set(false);
        this.rotating.set(false);
        this.statusIcon.set('check');
        this.statusLabel.set('login.connection_title');
        this.success.emit(user);
      },
      error: (err) => this.#handleError(err),
    });
  }

  #handleError(err: unknown): void {
    if (err instanceof Error && err.message === 'errors.unauthorized_access') {
      this.#toast.error(
        this.#translate.instant('login.toast.unauthorized_access'),
        this.#translate.instant('login.toast.error_title'),
      );
      this.#flash('login.toast.unauthorized_access', true);
      return;
    }

    if (err === 'errors.invalid_credentials') {
      this.#toast.error(
        this.#translate.instant('login.toast.invalid_credentials'),
        this.#translate.instant('login.toast.invalid_credentials_title'),
      );
      this.#flash('login.toast.invalid_credentials_title', true);
      return;
    }

    if (err === 'errors.account_disabled') {
      this.#toast.error(
        this.#translate.instant('login.toast.account_disabled'),
        this.#translate.instant('login.toast.account_disabled_title'),
      );
      this.#flash('login.toast.account_disabled_title', true);
      return;
    }

    this.#toast.error(
      this.#translate.instant('login.toast.generic_error'),
      this.#translate.instant('login.toast.error_title'),
    );
    this.#flash('login.toast.generic_error', true);
  }

  #flash(statusLabelKey: string, rejectCredentials: boolean): void {
    this.statusIcon.set('error');
    this.statusLabel.set(statusLabelKey);
    this.working.set(true);
    this.rotating.set(false);
    if (rejectCredentials) this.credentialsRejected.set(true);

    setTimeout(() => {
      this.working.set(false);
      this.credentialsRejected.set(false);
      this.statusIcon.set('check');
      this.statusLabel.set('login.connection_title');
    }, 1000);
  }
}
